/**
 * TransferRoomCard — lightweight deep-link wedge for the client homepage.
 *
 * Why this isn't a full <Wedge>:
 *   • TransferRoom partnership/API isn't signed yet — no real data to fetch.
 *   • Avoids any ToS risk that scraping or stubbing would create.
 *   • Single static card with a deep link pre-filled with the player's name.
 *
 * When the partner API agreement lands, we replace this card with a
 * `<Wedge provider="transferroom" />` and the backend endpoint that's
 * already scaffolded in /app/backend/wedges.py.
 */
import { ExternalLink, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

const TR_BASE = "https://www.transferroom.com";

function buildTransferRoomUrl(playerName) {
  // TransferRoom doesn't publicly document a deep-search URL, so we open the
  // homepage with a `q=` hint + an affiliate tag. The hint is also visible in
  // the URL bar so the user can paste it into TR's own search if needed.
  const q = encodeURIComponent((playerName || "").trim());
  return `${TR_BASE}/?ref=playmaker&q=${q}`;
}

export function TransferRoomCard({ client, onAffiliateClick }) {
  const playerName = client?.full_name || "this player";
  const href = buildTransferRoomUrl(client?.full_name);

  const handleClick = () => {
    // Best-effort affiliate-click log so we can see how often this drives traffic.
    if (onAffiliateClick) {
      onAffiliateClick("transferroom");
    } else {
      api
        .post("/integrations/affiliate-click", { provider: "transferroom", placement: "client_stats_wedge_deeplink" })
        .catch(() => {});
    }
  };

  return (
    <div className="pm-card overflow-hidden flex flex-col" data-testid="wedge-transferroom-deeplink">
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-2 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 border border-amber-200 flex items-center justify-center shrink-0 text-[11px] font-display font-black">
          TR
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm tracking-tight truncate flex items-center gap-1.5">
            TransferRoom
            <span className="pm-pill pm-pill-slate text-[10px]" data-testid="transferroom-status-pill">
              Deep link
            </span>
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400"
          data-testid="transferroom-roadmap-pill"
          title="Native two-way API is on the integrations roadmap"
        >
          Partner API soon
        </span>
      </div>

      {/* Body */}
      <div className="px-4 py-4 space-y-3 flex-1">
        <p className="text-[12px] text-slate-600 leading-relaxed">
          xTV valuations, availability signals and salary benchmarks live on TransferRoom.
          Open a pre-filled search for{" "}
          <span className="font-bold text-slate-900">{playerName}</span> in a new tab.
        </p>
        <div className="flex items-start gap-2 text-[11px] text-slate-500">
          <Sparkles size={12} className="text-amber-500 mt-0.5 shrink-0" />
          <span>
            We&rsquo;re negotiating a partner API agreement &mdash; when it lands, this card
            upgrades to live xTV + availability data without any change for you.
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1.5"
          data-testid="transferroom-deeplink-cta"
        >
          View on TransferRoom <ExternalLink size={11} />
        </a>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
          new tab
        </span>
      </div>
    </div>
  );
}

export default TransferRoomCard;
